/**
 * Pure, dependency-free converter: a legacy SOP record → Tiptap JSON document.
 * Shared by the in-app "migrate on edit" path (imported by webpack) and the
 * one-time batch script (required by Node), so the two can never diverge.
 *
 * It NEVER mutates the input and produces only a `contentJSON` document — the
 * caller decides where/whether to persist it. Legacy fields are untouched.
 *
 * Handles every shape the old editor produced:
 *   - descriptionItems / subItems entries that are raw strings OR {text, subItems}
 *   - arrays that Firestore returned as maps ({ "0": …, "1": … })
 *   - missing / empty fields
 *
 * Legacy shape:
 *   { description, steps: [ { title, description, descriptionItems: [...] } ] }
 * Mapping:
 *   description        → paragraph(s)
 *   step.title         → stepTitle (inside stepSection)
 *   step.description   → paragraph(s) in stepBody
 *   descriptionItems   → bulletList
 *   nested subItems    → nested bulletList
 */

/** Firestore sometimes returns arrays as maps; normalize both to a real array. */
export function normalizeList(val) {
  if (val == null) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === 'object') {
    const keys = Object.keys(val)
      .filter((k) => /^\d+$/.test(k))
      .sort((a, b) => Number(a) - Number(b));
    if (keys.length) return keys.map((k) => val[k]);
  }
  return [];
}

/** Extract display text from a string-or-object list node. */
function nodeText(node) {
  if (node == null) return '';
  if (typeof node === 'string') return node;
  if (typeof node === 'object') return node.text != null ? node.text : node.label != null ? node.label : '';
  return String(node);
}

function textNode(str) {
  return { type: 'text', text: str };
}

/** Split a paragraph string on blank lines into one or more paragraph nodes. */
export function paragraphsFrom(str) {
  const raw = (str == null ? '' : String(str)).replace(/\r\n/g, '\n');
  const parts = raw
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  return parts.map((p) => ({ type: 'paragraph', content: [textNode(p)] }));
}

/** Recursively build a bulletList (with nested bulletLists) from a list value. */
export function bulletListFrom(items) {
  const list = normalizeList(items);
  const listItems = list
    .map((item) => {
      const text = nodeText(item).trim();
      const subItems = typeof item === 'string' ? [] : normalizeList(item.subItems);
      const content = [text ? { type: 'paragraph', content: [textNode(text)] } : { type: 'paragraph' }];
      const nested = bulletListFrom(subItems);
      if (nested) content.push(nested);
      return { type: 'listItem', content };
    })
    // drop entries that are entirely empty (no text and no children)
    .filter((li) => {
      const para = li.content[0];
      const hasText = para && para.content && para.content.length;
      const hasNested = li.content.length > 1;
      return hasText || hasNested;
    });
  return listItems.length ? { type: 'bulletList', content: listItems } : null;
}

/** A step becomes a flat run of blocks: H2 title, then its paragraphs + list. */
function stepToBlocks(step) {
  const blocks = [];
  const title = (step && step.title ? String(step.title) : '').trim();
  blocks.push({ type: 'heading', attrs: { level: 2 }, content: title ? [textNode(title)] : [] });
  paragraphsFrom(step && step.description).forEach((p) => blocks.push(p));
  const list = bulletListFrom(step && step.descriptionItems);
  if (list) blocks.push(list);
  return blocks;
}

/**
 * Convert a full legacy SOP record to a plain Tiptap JSON document — one flowing
 * doc of headings, paragraphs and lists (no collapsible step nodes).
 * @param {object} record - a raw SOP doc (sopTemplates / account sops / customization)
 * @returns {{type:'doc', content:Array}}
 */
export function migrateSopRecord(record) {
  const content = [];
  paragraphsFrom(record && record.description).forEach((p) => content.push(p));
  normalizeList(record && record.steps).forEach((step) => {
    stepToBlocks(step).forEach((b) => content.push(b));
  });
  if (content.length === 0) content.push({ type: 'paragraph' });
  return { type: 'doc', content };
}

/**
 * Flatten an older step-format Tiptap doc (stepSection/stepTitle/stepBody, from
 * the first editor version) into the plain document shape: each step title
 * becomes an H2 heading and its body blocks are lifted to the top level. All
 * text — including tailored edits — is preserved. IDEMPOTENT: a doc that already
 * has no step nodes passes through unchanged, so it's safe to call on any doc.
 */
function flattenStepNode(node) {
  if (!node || typeof node !== 'object') return node ? [node] : [];
  if (node.type === 'stepSection') {
    const out = [];
    (node.content || []).forEach((child) => {
      if (child && child.type === 'stepTitle') {
        out.push({ type: 'heading', attrs: { level: 2 }, content: child.content || [] });
      } else if (child && child.type === 'stepBody') {
        (child.content || []).forEach((b) => flattenStepNode(b).forEach((n) => out.push(n)));
      } else {
        flattenStepNode(child).forEach((n) => out.push(n));
      }
    });
    return out;
  }
  if (Array.isArray(node.content) && node.content.some((c) => c && c.type === 'stepSection')) {
    const content = [];
    node.content.forEach((c) => flattenStepNode(c).forEach((n) => content.push(n)));
    return [{ ...node, content }];
  }
  return [node];
}

export function flattenStepContent(doc) {
  if (!doc || !Array.isArray(doc.content)) {
    return { type: 'doc', content: [{ type: 'paragraph' }] };
  }
  const content = [];
  doc.content.forEach((n) => flattenStepNode(n).forEach((x) => content.push(x)));
  return { type: 'doc', content: content.length ? content : [{ type: 'paragraph' }] };
}
